import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";
import PlacementBoard from "./PlacementBoard";

export default function Adverts({ embedded }) {
  const [members, setMembers] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => {
    client.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
    client.get("/admin/ad-plan").then((r) => setPlan(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const pull = async (ad) => {
    try { await client.post(`/admin/members/${ad.member_id}/advert/run`, { action: "unpublish" }); toast.success(`${ad.business_name}'s advert taken off.`); load(); }
    catch { toast.error("Could not take the advert off."); }
  };

  const gen = async (m) => {
    setBusy(m.id);
    try { await client.post(`/admin/members/${m.id}/generate-advert`); toast.success(`Advert written for ${m.business_name}. Publish or schedule its run in their Member Hub.`); await load(); }
    catch { toast.error("Advert generation failed."); }
    setBusy(null);
  };

  const withAd = members.filter((m) => (m.package || {}).advert?.headline);
  const without = members.filter((m) => !(m.package || {}).advert?.headline);

  return (
    <div>
      {!embedded && (
        <Header kicker="The Ad Engine" title="Adverts">
          <span className="overline text-ink/50">{withAd.length} live-ready · {without.length} to write</span>
        </Header>
      )}
      <div className={embedded ? "py-8 space-y-10" : "p-6 sm:p-10 space-y-10"}>
        {embedded && <p className="overline text-ink/50 -mb-4">{withAd.length} live-ready · {without.length} to write</p>}
        <PlacementBoard />
        {plan && (
          <div data-testid="ad-plan">
            <p className="overline text-gloss-silver border-b border-ink/15 pb-2 mb-1">The Ad Plan — {plan.cap} spaces per edition</p>
            <p className="font-sans text-xs text-ink/50 mb-4">16 named spaces: 5 on the homepage, one on every section page, and 4 inside content. Each space holds exactly ONE member's advert, FIXED for the full fortnight — pin them on the Placement Board above or from each Member Hub. Empty spaces collapse on the site; the only S/ME house ad is the small strip at the foot of the homepage.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {plan.editions.map((e) => (
                <div key={e.issue} className={`border p-5 ${e.current ? "border-gloss-gold bg-gloss-gold/5" : "border-ink/15 bg-white"}`} data-testid={`ad-plan-${e.issue_no}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-serif text-xl">Issue Nº {String(e.issue_no).padStart(2, "0")} {e.current && <span className="overline text-gloss-gold ml-1">Current</span>}</p>
                    <span className={`overline ${e.counts.total > plan.cap ? "text-red-600" : "text-ink/50"}`}>{e.counts.total}/{plan.cap}</span>
                  </div>
                  <p className="font-mono text-[11px] text-ink/45 mt-1">Starts {e.starts} · {e.counts.total} of {plan.cap} spaces taken</p>
                  <div className="mt-3 space-y-1.5">
                    {e.ads.map((ad) => (
                      <div key={ad.member_id} className="flex items-center gap-2" data-testid={`ad-plan-item-${ad.member_id}`}>
                        <span className={`overline px-2 py-0.5 shrink-0 ${ad.status === "live" ? "bg-ink text-cream" : "border border-ink/30 text-ink/60"}`}>{ad.status}</span>
                        <span className="font-serif text-sm flex-1 truncate">{ad.business_name}</span>
                        <span className="overline text-ink/45 shrink-0">{plan.slots[ad.slot]?.label || ad.slot}</span>
                        <button onClick={() => pull(ad)} title="Take off / unschedule" className="p-1 text-ink/40 hover:text-red-500 transition-colors" data-testid={`ad-plan-pull-${ad.member_id}`}><X size={14} /></button>
                      </div>
                    ))}
                    {e.ads.length === 0 && <p className="font-sans text-xs text-ink/40">Empty — spaces collapse on the site until adverts are pinned.</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="adverts-grid">
          {withAd.map((m) => {
            const a = m.package.advert;
            const website = m.website ? (/^https?:\/\//i.test(m.website) ? m.website : `https://${m.website}`) : null;
            return (
              <div key={m.id} className="text-cream p-6 flex flex-col grain relative overflow-hidden" style={{ backgroundColor: a.accent || "#141414" }} data-testid={`advert-card-${m.id}`}>
                <div className="relative z-10 flex items-center justify-between mb-3">
                  <span className="overline text-cream/60">{m.business_name} · {m.borough}</span>
                  <Badge status={m.status} />
                </div>
                <h3 className="relative z-10 font-serif text-2xl sm:text-3xl leading-tight">{a.headline}</h3>
                {a.subhead && <p className="relative z-10 font-grotesk text-base text-cream/85 mt-2">{a.subhead}</p>}
                <p className="relative z-10 font-sans text-sm text-cream/70 mt-3 flex-1">{a.body}</p>
                <div className="relative z-10 flex flex-wrap items-center gap-3 mt-5">
                  <span className="overline border border-cream/50 px-3 py-1.5">{a.cta || "Discover"}</span>
                  {website
                    ? <a href={website} target="_blank" rel="noreferrer" className="overline flex items-center gap-1.5 text-cream/80 hover:text-cream underline underline-offset-2" data-testid={`advert-website-${m.id}`}>{m.website} <ExternalLink size={12} /></a>
                    : <span className="overline text-cream/50">No website — add one on their profile</span>}
                  <button onClick={() => gen(m)} disabled={busy === m.id} className="overline flex items-center gap-1.5 border border-cream/50 px-3 py-1.5 hover:bg-cream hover:text-ink transition-colors ml-auto disabled:opacity-60" data-testid={`advert-regen-${m.id}`}>
                    {busy === m.id ? <RefreshCw size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate
                  </button>
                </div>
              </div>
            );
          })}
          {withAd.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50 lg:col-span-2">No adverts written yet — generate one for a member below.</p>}
        </div>

        {without.length > 0 && (
          <div>
            <p className="overline text-gloss-silver border-b border-ink/15 pb-2 mb-4">Members without an advert</p>
            <div className="border border-ink/15" data-testid="adverts-todo">
              {without.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-4 px-5 py-4 border-b border-ink/10 last:border-0">
                  <div>
                    <p className="font-serif text-lg">{m.business_name}</p>
                    <p className="font-sans text-sm text-ink/60">{m.owner_name} · {m.borough}</p>
                  </div>
                  <button onClick={() => gen(m)} disabled={busy === m.id} className="bg-ink text-cream px-4 py-2 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`advert-gen-${m.id}`}>
                    {busy === m.id ? <RefreshCw size={14} className="animate-spin" /> : <Megaphone size={14} />} Generate advert
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Notices ---------------- */
